import { useCallback, useState, type ReactNode } from 'react';
import { ChevronsRight } from 'lucide-react';
import { cn } from '@clarix/ui';
import { HomeChatList } from './HomeChatList';
import { HomeComingSoon } from './HomeComingSoon';
import { HomeComposer } from './HomeComposer';
import { HomeGreeting } from './HomeGreeting';
import { HomeHero } from './HomeHero';
import type { HomeChatItem, HomeComingSoonContent, HomeStarter } from './types';

export interface AppHomeProps {
  // ── Greeting ──
  /** Full name; only the first word is shown. */
  userName?: string;
  /** Overrides the clock-derived greeting. */
  greeting?: string;
  /** Drop the greeting when the surrounding chrome already shows one. */
  showGreeting?: boolean;

  // ── Hero ──
  /** Assistant name set as the wordmark. Defaults to the shared assistant. */
  title?: string;
  subtitle?: string;
  /** Defaults to the shared openers — override only for app-specific ones. */
  starters?: readonly HomeStarter[];

  // ── Composer ──
  /** Fires for every ask — typed or tapped from a starter. Optional: with no
   *  assistant wired up yet, the announcement is the whole behaviour. */
  onSend?: (text: string) => void;
  /**
   * Asking for something swaps the hero for an app-neutral announcement.
   * Pass this ONLY to override that shared copy — it is not needed for the
   * default screen, which every app shows identically.
   */
  comingSoon?: HomeComingSoonContent;
  /** Opt out entirely and own the surface yourself via `children`. */
  disableComingSoon?: boolean;
  /**
   * localStorage key remembering that something was asked, so a reload keeps
   * showing the announcement instead of reverting to the hero. Give each app
   * its own key. Pass `null` to keep the state in memory only.
   */
  persistKey?: string | null;
  /** Both default to shared copy — an app passing its own is how the home
   *  screens drift apart, so override only with a real reason. */
  placeholder?: string;
  hint?: ReactNode;
  onAttach?: () => void;
  onAdd?: () => void;
  onDictate?: () => void;
  isBusy?: boolean;

  // ── Conversation history ──
  /** Omit entirely to render the home without the side panel. */
  chats?: readonly HomeChatItem[];
  activeChatId?: string;
  onSelectChat?: (chat: HomeChatItem) => void;
  onNewChat?: () => void;

  /**
   * App-owned content for the surface. While this is empty the hero shows;
   * once anything is rendered here the hero steps aside and the wash fades.
   */
  children?: ReactNode;
  className?: string;
}

const DEFAULT_PERSIST_KEY = 'clarix.app-home.asked';

/**
 * Shared defaults, so every app gets the identical home. Kept module-private on
 * purpose: an app composing its own labels from these would be forking the one
 * home screen, and would couple itself to a named export of a shared barrel.
 */
const DEFAULT_TITLE = 'Clara';
const DEFAULT_SUBTITLE = 'What do you want to get done today?';
const DEFAULT_STARTERS: readonly HomeStarter[] = [
  { id: 'plan-day', label: 'Plan my day' },
  { id: 'overdue', label: "What's overdue?" },
  { id: 'unfinished', label: "What didn't I finish?" },
  { id: 'add', label: 'Add something' },
];
const DEFAULT_PLACEHOLDER = 'Type if you like — tapping is usually faster.';
const DEFAULT_HINT = 'Enter to send · Shift + Enter for a new line';

/**
 * Whether something has already been asked on this surface.
 *
 * Storage can throw (private mode, blocked cookies) and must never take the
 * home down with it, so both reads and writes fail quietly back to memory.
 */
function readAsked(key: string | null): boolean {
  if (!key || typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeAsked(key: string | null, value: boolean): void {
  if (!key || typeof window === 'undefined') return;
  try {
    if (value) window.localStorage.setItem(key, '1');
    else window.localStorage.removeItem(key);
  } catch {
    /* private mode — the surface just won't remember across reloads */
  }
}

/**
 * The common home surface, shared by every app.
 *
 * One screen: the greeting, the assistant wordmark, the question it asks, a
 * few starters so nobody faces a blank box, and a composer. Every app mounts it
 * through `ClarixHome`, which passes only the name — nothing here is app-specific.
 *
 * Asking for something does NOT open a chat. Until an app can genuinely act,
 * the hero gives way to an app-neutral announcement (`HomeComingSoon`), and
 * that choice survives a reload (see `persistKey`).
 */
export function AppHome({
  userName,
  greeting,
  showGreeting = true,
  title = DEFAULT_TITLE,
  subtitle = DEFAULT_SUBTITLE,
  starters = DEFAULT_STARTERS,
  onSend,
  comingSoon,
  disableComingSoon = false,
  persistKey = DEFAULT_PERSIST_KEY,
  placeholder = DEFAULT_PLACEHOLDER,
  hint = DEFAULT_HINT,
  onAttach,
  onAdd,
  onDictate,
  isBusy,
  chats,
  activeChatId,
  onSelectChat,
  onNewChat,
  children,
  className,
}: AppHomeProps) {
  const [historyCollapsed, setHistoryCollapsed] = useState(false);
  // True once anything has been asked — the announcement is showing. Read
  // back from storage on mount so a reload doesn't drop the person back on
  // the hero as if they had never asked.
  const [hasAsked, setHasAsked] = useState(() => readAsked(persistKey));

  const handleSend = useCallback(
    (text: string) => {
      onSend?.(text);
      if (disableComingSoon) return;
      setHasAsked(true);
      writeAsked(persistKey, true);
    },
    [onSend, disableComingSoon, persistKey],
  );

  const handleNewChat = useCallback(() => {
    setHasAsked(false);
    writeAsked(persistKey, false);
    onNewChat?.();
  }, [onNewChat, persistKey]);

  return (
    <div className={cn('relative flex h-full min-h-0 w-full', className)}>
      {chats ? (
        <HomeChatList
          chats={chats}
          activeChatId={activeChatId}
          onSelectChat={onSelectChat}
          onNewChat={handleNewChat}
          collapsed={historyCollapsed}
          onCollapse={() => setHistoryCollapsed(true)}
          // The panel overlays the surface on narrow screens instead of
          // squeezing the conversation column.
          className="max-lg:absolute max-lg:inset-y-0 max-lg:left-0 max-lg:z-30 max-lg:shadow-lg"
        />
      ) : null}

      {chats && historyCollapsed ? (
        <button
          type="button"
          onClick={() => setHistoryCollapsed(false)}
          title="Show chats"
          aria-label="Show chats"
          className="bg-surface text-muted-foreground hover:text-primary absolute left-2 top-16 z-40 grid h-[30px] w-[30px] place-items-center rounded-md shadow-md transition-colors"
        >
          <ChevronsRight className="h-[15px] w-[15px]" strokeWidth={1.5} />
        </button>
      ) : null}

      <div
        className={cn(
          'flex h-full min-w-0 flex-1 flex-col transition-colors duration-500',
          // The wash rising behind the composer belongs to the whole home —
          // the hero AND the announcement. Only an app rendering its own
          // content gets the plain canvas.
          children
            ? 'bg-background'
            : 'bg-[radial-gradient(ellipse_100%_55%_at_bottom_center,rgb(var(--indigo-400)/0.26)_0%,rgb(var(--indigo-500)/0.13)_45%,transparent_75%)]',
        )}
      >
        <div className="min-h-0 flex-1 overflow-y-auto">
          {hasAsked ? (
            <HomeComingSoon {...comingSoon} />
          ) : children ? (
            <div className="mx-auto max-w-[760px] px-6 pb-4 pt-5">{children}</div>
          ) : (
            <HomeHero
              // The home's own title, centred directly above the wordmark —
              // greeting only, sized to read with it as one lockup.
              greeting={
                showGreeting ? (
                  <HomeGreeting
                    size="hero"
                    align="center"
                    userName={userName}
                    greeting={greeting}
                  />
                ) : null
              }
              title={title}
              subtitle={subtitle}
              starters={starters}
              // A starter is the same ask, tapped instead of typed.
              onStarterSelect={(starter) => handleSend(starter.prompt ?? starter.label)}
            />
          )}
        </div>

        <HomeComposer
          onSend={handleSend}
          placeholder={placeholder}
          hint={hint}
          onAttach={onAttach}
          onAdd={onAdd}
          onDictate={onDictate}
          isBusy={isBusy}
        />
      </div>
    </div>
  );
}
